import React from 'react';
import { Card, LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import { FiPieChart } from 'react-icons/fi';
export const STATUS_COLORS = {
  1: '#3B82F6', // Submitted
  2: '#F59E0B', // Waiting for Approval
  3: '#10B981', // Approved
  4: '#EF4444', // Rejected
  5: '#94A3B8', // Canceled
};

const fmt = (n) => Number(n || 0).toLocaleString('en-US');

// SVG donut of waiver counts per approval status.
// Clicking a slice / legend row drills into the underlying records.
const WaiverStatusChart = ({ data, loading, error, onDrillDown }) => {
  const drill = (s) => onDrillDown && onDrillDown(`Status: ${s.status}`, { statuses: [s.status_id] });
  const items = (data || []).filter(d => d.count > 0);
  const total = items.reduce((s, d) => s + d.count, 0);

  const cx = 90, cy = 90, outerR = 80, innerR = 50;
  let angle = -Math.PI / 2;
  const slices = items.map((item) => {
    const sweep = (item.count / total) * 2 * Math.PI;
    const s = angle, e = angle + sweep;
    angle = e;
    const large = sweep > Math.PI ? 1 : 0;
    const x1 = cx + outerR * Math.cos(s), y1 = cy + outerR * Math.sin(s);
    const x2 = cx + outerR * Math.cos(e), y2 = cy + outerR * Math.sin(e);
    const x3 = cx + innerR * Math.cos(e), y3 = cy + innerR * Math.sin(e);
    const x4 = cx + innerR * Math.cos(s), y4 = cy + innerR * Math.sin(s);
    const path = `M${x1} ${y1} A${outerR} ${outerR} 0 ${large} 1 ${x2} ${y2} L${x3} ${y3} A${innerR} ${innerR} 0 ${large} 0 ${x4} ${y4} Z`;
    return { ...item, path, color: STATUS_COLORS[item.status_id] || '#94A3B8' };
  });

  return (
    <Card padding="none" className="tw-border tw-border-gray-200 tw-shadow-sm tw-h-full">
      <div className="tw-px-5 tw-py-3 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-gap-2">
        <FiPieChart size={15} className="tw-text-primary-500" />
        <h3 className="tw-font-bold tw-text-gray-900 tw-text-sm tw-mb-0">Status of Waivers</h3>
      </div>

      {loading ? (
        <div className="tw-flex tw-justify-center tw-py-10"><LoadingSpinner size="md" /></div>
      ) : error || total === 0 ? (
        <p className="tw-text-xs tw-text-gray-400 tw-text-center tw-py-10">No waiver data</p>
      ) : (
        <div className="tw-flex tw-items-center tw-gap-6 tw-p-5 tw-flex-wrap">
          <svg width={180} height={180} className="tw-flex-shrink-0">
            {slices.map(s => (
              <path key={s.status_id} d={s.path} fill={s.color} opacity={0.9}
                onClick={() => drill(s)} style={onDrillDown ? { cursor: 'pointer' } : undefined}>
                <title>{`${s.status}: ${fmt(s.count)}`}</title>
              </path>
            ))}
            <text x={cx} y={cy - 4} textAnchor="middle" className="tw-fill-gray-900" fontSize="22" fontWeight="700">{fmt(total)}</text>
            <text x={cx} y={cy + 14} textAnchor="middle" className="tw-fill-gray-400" fontSize="10">waivers</text>
          </svg>

          <div className="tw-flex-1 tw-min-w-[160px] tw-space-y-2">
            {(data || []).map(s => (
              <div key={s.status_id}
                onClick={() => s.count > 0 && drill(s)}
                className={`tw-flex tw-items-center tw-gap-2 tw-rounded-md tw-px-1 -tw-mx-1 ${onDrillDown && s.count > 0 ? 'tw-cursor-pointer hover:tw-bg-slate-50' : ''}`}>
                <span className="tw-w-2.5 tw-h-2.5 tw-rounded-xl tw-shrink-0" style={{ background: STATUS_COLORS[s.status_id] }} />
                <span className="tw-text-xs tw-text-gray-600 tw-flex-1">{s.status}</span>
                <span className="tw-text-xs tw-font-bold tw-text-gray-900 tw-tabular-nums">{fmt(s.count)}</span>
                <span className="tw-text-[10px] tw-text-gray-400 tw-w-10 tw-text-right tw-tabular-nums">
                  {total > 0 ? `${((s.count / total) * 100).toFixed(0)}%` : '0%'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};

WaiverStatusChart.propTypes = {
  data: PropTypes.array, loading: PropTypes.bool, error: PropTypes.any,
  onDrillDown: PropTypes.func,
};
WaiverStatusChart.defaultProps = { data: [], loading: false, error: null, onDrillDown: null };

export default WaiverStatusChart;
